import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, fromEvent, map, startWith } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { Database, ref as dbRef, get as dbGet } from '@angular/fire/database';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppContextServiceService {
  private destroyRef = inject(DestroyRef);
  private http = inject(HttpClient);
  constructor(){
    fromEvent(window, 'resize').pipe(
      map(() => window.innerWidth < 768),
      startWith(window.innerWidth < 768),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(isMob => this.isMobile.set(isMob));
  }
  readonly isMobile = signal<boolean>(window.innerWidth < 768);

  private _authedUser$ = new BehaviorSubject<boolean | null | undefined>(undefined);
  private _currentUserId$ = new BehaviorSubject<string | null | undefined>(undefined);
  private _userUnits$ = new BehaviorSubject<any[] | null | undefined>(undefined);
  private _selectedUnit$ = new BehaviorSubject<string | null | undefined>(undefined);
  private _clientName$ = new BehaviorSubject<string | null | undefined>(undefined);
  private _clientDisplayTimeZone$ = new BehaviorSubject<string>('Europe/Stockholm');
  private _clientDisplayTimeZone = signal<string>('Europe/Stockholm');

  usingNewOrderFlow = false;
  apiUrl = "";

  // Firebase services
  private auth = inject(Auth);
  private db = inject(Database);

  // Getter and setters for authedUser
  get authedUser(): boolean | null | undefined {
    return this._authedUser$.getValue();
  }
  set authedUser(val: boolean | null | undefined) {
    this._authedUser$.next(val);
  }
  get authedUser$() {
    return this._authedUser$.asObservable();
  }

  // Getter and setters for currentUserId
  get currentUserId(): string | null | undefined {
    return this._currentUserId$.getValue();
  }
  set currentUserId(val: string | null | undefined) {
    this._currentUserId$.next(val);
  }
  get currentUserId$() {
    return this._currentUserId$.asObservable();
  }

  // Getter and setters for userUnits
  get userUnits(): any[] | null | undefined {
    return this._userUnits$.getValue();
  }
  set userUnits(units: any[] | null | undefined) {
    this._userUnits$.next(units);
  }
  get userUnits$() {
    return this._userUnits$.asObservable();
  }

  // Getter and setters for selectedUnit
  get selectedUnit(): string | null | undefined {
    return this._selectedUnit$.getValue();
  }
  set selectedUnit(unit: string | null | undefined) {
    this._selectedUnit$.next(unit);
  }
  get selectedUnit$() {
    return this._selectedUnit$.asObservable();
  }

  // Getter and setters for clientName (observable-based, like userUnits)
  get clientName(): string | null | undefined {
    return this._clientName$.getValue();
  }
  set clientName(val: string | null | undefined) {
    this._clientName$.next(val);
  }
  get clientName$() {
    return this._clientName$.asObservable();
  }

  get clientDisplayTimeZone(): string {
    return this._clientDisplayTimeZone();
  }
  get clientDisplayTimeZoneSignal() {
    return this._clientDisplayTimeZone.asReadonly();
  }
  get clientDisplayTimeZone$() {
    return this._clientDisplayTimeZone$.asObservable();
  }

  async resolveClientDisplayTimeZone(routeName: string): Promise<void> {
    if (!routeName) return;
    const url = `${environment.pubQueueApiUrl}/settings/v1/getResolvedClientTimeZone`;
    try {
      const tz = await firstValueFrom(
        this.http.post(url, { routeName }, { responseType: 'text' })
      );
      const value = (tz || '').trim();
      if (value) {
        this.setClientDisplayTimeZone(value);
      } else {
        this.applyRouteNameTimeZoneFallback(routeName);
      }
    } catch {
      this.applyRouteNameTimeZoneFallback(routeName);
    }
  }

  private applyRouteNameTimeZoneFallback(routeName: string): void {
    const r = (routeName || '').toLowerCase();
    if (r.startsWith('compassfi')) this.setClientDisplayTimeZone('Europe/Helsinki');
    else if (r.startsWith('compassdk')) this.setClientDisplayTimeZone('Europe/Copenhagen');
    else if (r.startsWith('compassno')) this.setClientDisplayTimeZone('Europe/Oslo');
    else this.setClientDisplayTimeZone('Europe/Stockholm');
  }

  private setClientDisplayTimeZone(value: string): void {
    this._clientDisplayTimeZone.set(value);
    this._clientDisplayTimeZone$.next(value);
  }

}